import React, { useState, useEffect } from 'react';
import { AlertCircle, Calendar, ChevronDown, ChevronRight, ChevronUp, Search } from 'lucide-react';
import { useApi } from '../hooks/useApi';

function MarketInfluence() {
  const { axiosPost } = useApi();

  // Filled from the first response: the backend picks the last 14 days with data.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [available, setAvailable] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const toggleDay = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const displayHour = (hour) => displayHours1to24 ? hour + 1 : hour;

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Without dates the backend answers with its default range (the latest data).
  const handleShowInfluence = async ({ useDefaultRange = false } = {}) => {
    if (!useDefaultRange && (!startDate || !endDate)) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setExpandedDays({});

    try {
      const response = await axiosPost(
        '/get-market-influence',
        useDefaultRange ? {} : { start_date: startDate, end_date: endDate }
      );

      if (response.data.status === 'success') {
        setResults(response.data);
        setAvailable(response.data.available || null);
        if (useDefaultRange) {
          setStartDate(response.data.start_date);
          setEndDate(response.data.end_date);
        }
      } else {
        setError(response.data.message || 'Failed to fetch market influence data');
      }
    } catch (err) {
      console.error('Error fetching market influence:', err);
      setError(err.message || 'Failed to fetch market influence data');
    } finally {
      setLoading(false);
    }
  };

  // Load the latest data on mount
  useEffect(() => {
    handleShowInfluence({ useDefaultRange: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInfluenceColor = (influence) => {
    if (influence === null || influence === undefined) return 'text-gray-500';
    if (influence > 0) return 'text-green-600'; // Raised price - good
    if (influence < 0) return 'text-red-600'; // Lowered price - bad
    return 'text-gray-500';
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort daily results
  const getSortedDaily = () => {
    if (!results || !results.daily) return [];
    return [...results.daily].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'date':
          aVal = a.date;
          bVal = b.date;
          break;
        case 'fix_i_price':
          aVal = a.summary.avg_fixing_i_price || 0;
          bVal = b.summary.avg_fixing_i_price || 0;
          break;
        case 'fix_i_influence':
          aVal = a.summary.avg_fixing_i_influence || 0;
          bVal = b.summary.avg_fixing_i_influence || 0;
          break;
        case 'fix_i_realized':
          aVal = a.summary.total_fixing_i_realized_volume || 0;
          bVal = b.summary.total_fixing_i_realized_volume || 0;
          break;
        case 'fix_ii_price':
          aVal = a.summary.avg_fixing_ii_price || 0;
          bVal = b.summary.avg_fixing_ii_price || 0;
          break;
        case 'fix_ii_influence':
          aVal = a.summary.avg_fixing_ii_influence || 0;
          bVal = b.summary.avg_fixing_ii_influence || 0;
          break;
        default:
          aVal = a.date;
          bVal = b.date;
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  };

  // Sortable header component
  const SortableHeader = ({ column, label, align = 'right', className = '' }) => (
    <th
      className={`px-4 py-3 text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${align === 'left' ? 'text-left' : 'text-right'} ${className}`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === column && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
          <ChevronDown className={`w-3 h-3 -mt-1 ${sortColumn === column && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
        </div>
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Market Influence</h1>
          <p className="text-gray-600">Analyze how your orders affected fixing prices</p>
          {available && (
            <p className="text-sm text-gray-500 mt-1">
              Data: Fix I {available.fix_i ? `${available.fix_i.first} – ${available.fix_i.last}` : 'none'}
              {' · '}
              Fix II {available.fix_ii ? `${available.fix_ii.first} – ${available.fix_ii.last}` : 'none'}
              {' · '}
              Fix I days marked 15 min clear in quarters; their hour is the mean of the four quarter prices.
            </p>
          )}
        </div>

        {/* Parameters Form */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Start Date */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Show Influence Button - new row */}
          <div className="mt-6">
            <button
              onClick={() => handleShowInfluence()}
              disabled={loading || !startDate || !endDate}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Show Influence</span>
                </>
              )}
            </button>
          </div>

          {/* Hour Display Toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {results && results.daily && results.daily.length > 0 && (
          <>
            {/* Daily Results Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider w-8"></th>
                    <SortableHeader column="date" label="Date" align="left" className="text-gray-600" />
                    {/* Fixing I Section */}
                    <SortableHeader column="fix_i_price" label="Fix I Price" className="text-gray-600" />
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">No-Self-Impact</th>
                    <SortableHeader column="fix_i_influence" label="Influence" className="text-gray-600" />
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Fix I Vol</th>
                    <SortableHeader column="fix_i_realized" label="Realized Vol" className="text-gray-600" />
                    {/* Fixing II Section - visually separated */}
                    <SortableHeader column="fix_ii_price" label="Fix II Price" className="text-blue-600 bg-blue-50/70 border-l border-blue-200" />
                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">NP Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">No-Self-Impact</th>
                    <SortableHeader column="fix_ii_influence" label="Influence" className="text-blue-600 bg-blue-50/70" />
                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">Realized Vol</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {getSortedDaily().map((day, index) => {
                    const isExpanded = expandedDays[day.date];
                    return (
                      <React.Fragment key={day.date}>
                        {/* Summary Row */}
                        <tr
                          className={`group cursor-pointer ${index % 2 === 1 ? 'bg-gray-50' : ''}`}
                          onClick={() => toggleDay(day.date)}
                        >
                          <td className="px-4 py-3 group-hover:bg-gray-100">
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 group-hover:bg-gray-100 whitespace-nowrap">
                            {day.date}
                            {day.resolution === 'PT15M' && (
                              <span className="ml-2 px-1.5 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">15 min</span>
                            )}
                          </td>
                          {/* Fixing I Section */}
                          <td className="px-4 py-3 text-right text-gray-700 group-hover:bg-gray-100">
                            {formatNumber(day.summary.avg_fixing_i_price)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 group-hover:bg-gray-100">
                            {formatNumber(day.summary.avg_fixing_i_no_self_impact)}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium group-hover:bg-gray-100 ${getInfluenceColor(day.summary.avg_fixing_i_influence)}`}>
                            {formatNumber(day.summary.avg_fixing_i_influence)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 group-hover:bg-gray-100">
                            {formatNumber(day.summary.total_fixing_i_volume, 1)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 group-hover:bg-gray-100">
                            {formatNumber(day.summary.total_fixing_i_realized_volume, 1)}
                          </td>
                          {/* Fixing II Section - visually separated */}
                          <td className={`px-4 py-3 text-right text-blue-700 border-l border-blue-200 group-hover:bg-blue-100/60 ${index % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                            {formatNumber(day.summary.avg_fixing_ii_price)}
                          </td>
                          <td className={`px-4 py-3 text-right text-blue-700 group-hover:bg-blue-100/60 ${index % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                            {formatNumber(day.summary.avg_fixing_ii_price_np)}
                          </td>
                          <td className={`px-4 py-3 text-right text-blue-700 group-hover:bg-blue-100/60 ${index % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                            {formatNumber(day.summary.avg_fixing_ii_no_self_impact)}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium group-hover:bg-blue-100/60 ${index % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'} ${getInfluenceColor(day.summary.avg_fixing_ii_influence)}`}>
                            {formatNumber(day.summary.avg_fixing_ii_influence)}
                          </td>
                          <td className={`px-4 py-3 text-right text-blue-700 group-hover:bg-blue-100/60 ${index % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                            {formatNumber(day.summary.total_fixing_ii_realized_volume, 1)}
                          </td>
                        </tr>

                        {/* Expanded Hourly Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="12" className="px-4 py-4 bg-gray-50">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Hour</th>
                                      {/* Fixing I Section */}
                                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Fix I Price</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">No-Self-Impact</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Influence</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Fix I Vol</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Realized Vol</th>
                                      {/* Fixing II Section - visually separated */}
                                      <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70 border-l border-blue-200">Fix II Price</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">NP Price</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">No-Self-Impact</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">Influence</th>
                                      <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50/70">Realized Vol</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {day.hours.map((hour, hourIndex) => (
                                      <tr
                                        key={hour.hour}
                                        className={`${hourIndex % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                                      >
                                        <td className="px-2 py-2 font-medium text-gray-900">
                                          {displayHour(hour.hour)}
                                        </td>
                                        {/* Fixing I Section */}
                                        <td className="px-2 py-2 text-right text-gray-700">
                                          {formatNumber(hour.fixing_i_price)}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-700">
                                          {formatNumber(hour.fixing_i_price_no_self_impact)}
                                        </td>
                                        <td className={`px-2 py-2 text-right font-medium ${getInfluenceColor(hour.fixing_i_influence)}`}>
                                          {formatNumber(hour.fixing_i_influence)}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-700">
                                          {formatNumber(hour.fixing_i_volume, 1)}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-700">
                                          {formatNumber(hour.fixing_i_realized_volume, 1)}
                                        </td>
                                        {/* Fixing II Section - visually separated */}
                                        <td className={`px-2 py-2 text-right text-blue-700 border-l border-blue-200 ${hourIndex % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                                          {formatNumber(hour.fixing_ii_price)}
                                        </td>
                                        <td className={`px-2 py-2 text-right text-blue-700 ${hourIndex % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                                          {formatNumber(hour.fixing_ii_price_np)}
                                        </td>
                                        <td className={`px-2 py-2 text-right text-blue-700 ${hourIndex % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                                          {formatNumber(hour.fixing_ii_price_no_self_impact)}
                                        </td>
                                        <td className={`px-2 py-2 text-right font-medium ${hourIndex % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'} ${getInfluenceColor(hour.fixing_ii_influence)}`}>
                                          {formatNumber(hour.fixing_ii_influence)}
                                        </td>
                                        <td className={`px-2 py-2 text-right text-blue-700 ${hourIndex % 2 === 1 ? 'bg-blue-100/30' : 'bg-blue-50/30'}`}>
                                          {formatNumber(hour.fixing_ii_realized_volume, 1)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* No Results Message */}
        {results && results.daily && results.daily.length === 0 && (
          <div className="bg-yellow-50 rounded-xl p-4 text-center">
            <p className="text-yellow-700">No market influence data found for the selected date range.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketInfluence;
